/**
 * One front door for "Career DNA™", resolved once and reused everywhere.
 *
 * Every surface that offers the walk (home cards, dashboard hero, the shell's
 * Career DNA™ tab) used to decide on its own where to send the learner. They
 * drifted: the dashboard could show "Retake", the history list could show
 * "View", and the Explorer card could still say "Find your Career DNA™" and
 * send someone who had already finished all the way back to the band picker.
 *
 * This hook is the single answer. It reads the two records of a run (attempts)
 * plus the Career DNA™ context, and returns one destination with copy that
 * matches it. It computes nothing about scores.
 */
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { fetchAttempts, type Attempt } from "@/lib/assessmentAttempts";
import { bandParamFor } from "@/components/home/AssessmentHistory";
import { useCareerDna } from "@/hooks/useCareerDna";

export type AssessmentEntryState = "none" | "in_progress" | "done";

export interface AssessmentEntry {
  state: AssessmentEntryState;
  /** Where a "Career DNA™" call to action must point right now. */
  to: string;
  /** Short action label. */
  label: string;
  /** One supporting sentence, in the learner's voice. */
  body: string;
  helper: string;
  /** Query-string band for the run being pointed at. */
  band: string;
  /** True once a finished run exists, sealed report or not. */
  hasResult: boolean;
  attempts: Attempt[];
  loading: boolean;
}

export function useAssessmentEntry(): AssessmentEntry {
  const { user } = useAuth();
  const { data: dna } = useCareerDna();

  const { data: attempts = [], isLoading } = useQuery({
    queryKey: ["assessment-attempts", user?.id ?? "anon"],
    enabled: Boolean(user?.id),
    staleTime: 60 * 1000,
    queryFn: () => fetchAttempts(user!.id),
  });

  const completed = attempts.find((a) => a.status === "scored" || a.status === "completed");
  const resume = attempts.find((a) => a.status === "in_progress");
  const hasResult = Boolean(completed) || Boolean(dna?.archetypePrimary);

  if (hasResult) {
    const band = bandParamFor(completed?.age_group ?? null);
    return {
      state: "done",
      to: `/assessment/v2/result?band=${encodeURIComponent(band)}`,
      label: "Explore your Career DNA™",
      body: "Your matches are ready — with the reason for each one.",
      helper: "Your results are ready.",
      band,
      hasResult,
      attempts,
      loading: isLoading,
    };
  }

  if (resume) {
    const band = bandParamFor(resume.age_group ?? null);
    return {
      state: "in_progress",
      to: `/assessment/v2?band=${encodeURIComponent(band)}`,
      label: "Pick up where you left off",
      body: "You're partway there. Your answers are saved.",
      helper: "You're partway there.",
      band,
      hasResult,
      attempts,
      loading: isLoading,
    };
  }

  return {
    state: "none",
    to: "/start",
    label: "Find your Career DNA™",
    body: "One short walk and your matches appear here — with the reason for each.",
    helper: "About 12 minutes. One question at a time.",
    band: "18+",
    hasResult,
    attempts,
    loading: isLoading,
  };
}
